import {
  CreateDependentProfileRequest,
  PersonalInfo,
  SavePersonalProfileRequest,
  SaveSpousalProfileRequest,
  UpdateDependentProfileRequest,
} from '@taxfyle/api-internal/internal/individual_profile_pb'
import { ContactInformation } from '@taxfyle/api-internal/shared/types/contact_info_pb'
import { PhysicalAddress } from '@taxfyle/api-internal/shared/types/physical_address_pb'
import { Id } from 'types/definitions'

export function mapPhysicalAddressToProto(
  input: PhysicalAddress.AsObject,
): PhysicalAddress {
  return new PhysicalAddress()
    .setAddressLine1(input.addressLine1)
    .setAddressLine2(input.addressLine2)
    .setCity(input.city)
    .setRegion(input.region)
    .setPostalCode(input.postalCode)
    .setCountry(input.country)
}

export function mapContactInfoToProto(
  input: ContactInformation.AsObject,
): ContactInformation {
  const proto = new ContactInformation()
    .setEmail(input.email)
    .setPhoneNumber(input.phoneNumber)
  if (input.address) {
    proto.setAddress(mapPhysicalAddressToProto(input.address))
  }
  return proto
}

export function mapPersonalInfoToProto(
  input: PersonalInfo.AsObject,
): PersonalInfo {
  return new PersonalInfo()
    .setFirstName(input.firstName)
    .setMiddleInitial(input.middleInitial)
    .setLastName(input.lastName)
    .setDateOfBirth(input.dateOfBirth)
    .setPersonalTaxpayerIdentificationNumber(
      input.personalTaxpayerIdentificationNumber,
    )
}

/**
 * Shape accepted by the request mapper. We use `Id` for identifier fields
 * (vs the proto's plain `string`) so callers don't have to pre-assert at every
 * site — the mapper itself runs `Id.assert()` at the proto boundary.
 */
export interface SavePersonalProfileInput {
  workspaceId: Id
  ownerUserId: Id
  occupation: string
  personalInfo?: PersonalInfo.AsObject
  contactInfo?: ContactInformation.AsObject
}

export function mapSavePersonalProfileRequestToProto(
  input: SavePersonalProfileInput,
): SavePersonalProfileRequest {
  const request = new SavePersonalProfileRequest()
    .setWorkspaceId(Id.assert(input.workspaceId))
    .setOwnerUserId(Id.assert(input.ownerUserId))
    .setOccupation(input.occupation)
  if (input.personalInfo) {
    request.setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
  }
  if (input.contactInfo) {
    request.setContactInfo(mapContactInfoToProto(input.contactInfo))
  }
  return request
}

export interface SaveSpousalProfileInput {
  workspaceId: Id
  ownerUserId: Id
  occupation: string
  personalInfo?: PersonalInfo.AsObject
  contactInfo?: ContactInformation.AsObject
}

export function mapSaveSpousalProfileRequestToProto(
  input: SaveSpousalProfileInput,
): SaveSpousalProfileRequest {
  const request = new SaveSpousalProfileRequest()
    .setWorkspaceId(Id.assert(input.workspaceId))
    .setOwnerUserId(Id.assert(input.ownerUserId))
    .setOccupation(input.occupation)
  if (input.personalInfo) {
    request.setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
  }
  if (input.contactInfo) {
    request.setContactInfo(mapContactInfoToProto(input.contactInfo))
  }
  return request
}

export interface CreateDependentProfileInput {
  workspaceId: Id
  ownerUserId: Id
  relationship: string
  personalInfo?: PersonalInfo.AsObject
}

export function mapCreateDependentProfileRequestToProto(
  input: CreateDependentProfileInput,
): CreateDependentProfileRequest {
  const request = new CreateDependentProfileRequest()
    .setWorkspaceId(Id.assert(input.workspaceId))
    .setOwnerUserId(Id.assert(input.ownerUserId))
    .setRelationship(input.relationship)
  if (input.personalInfo) {
    request.setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
  }
  return request
}

export interface UpdateDependentProfileInput {
  id: Id
  relationship: string
  personalInfo?: PersonalInfo.AsObject
}

export function mapUpdateDependentProfileRequestToProto(
  input: UpdateDependentProfileInput,
): UpdateDependentProfileRequest {
  const request = new UpdateDependentProfileRequest()
    .setId(Id.assert(input.id))
    .setRelationship(input.relationship)
  if (input.personalInfo) {
    request.setPersonalInfo(mapPersonalInfoToProto(input.personalInfo))
  }
  return request
}
